<template>
  <div v-if="modelValue" class="iq-overlay">
    <div class="iq-overlay-backdrop" @click="handleClose"></div>

    <!-- Header - Full Width -->
    <div class="iq-overlay-header">
      <IQHeader :logoShow="true" :size="'large'" :countNewMsg="0" @close="handleClose" />
    </div>

    <!-- Content Area - Split when showSuggestions, showCheckpointSuggestions, or showVideoInstructionsPane is true -->
    <div class="iq-overlay-body" :class="{ 'split-layout': showSuggestions || showCheckpointSuggestions || iqOverlayStore.showVideoInstructionsPane }">
      <!-- Left Side: Chat -->
      <div class="iq-chat-section" :class="{ 
        'with-suggestions': showSuggestions || showCheckpointSuggestions, 
        'with-video-instructions': iqOverlayStore.showVideoInstructionsPane && !hasVideoInstructionsError,
        'with-video-instructions-error': iqOverlayStore.showVideoInstructionsPane && hasVideoInstructionsError
      }">
        <!-- Initial Welcome State -->
        <div v-if="!chatStore.hasMessages" class="iq-welcome-container">
          <div class="iq-welcome-content">
            <IQAskAnythingHeader :text="welcomeText" />
            <div class="iq-search-center">
              <IQSearch @submit="handleSubmit" :isGenerating="chatStore.isGenerating || chatStore.isLoading"
                @stop="handleStop" />
            </div>
          </div>
        </div>

        <!-- Chat State -->
        <div v-else class="iq-chat-container">
          <div class="iq-chat-messages" ref="messagesContainer">
            <!-- Chat Messages -->
            <div v-for="message in chatStore.messages" :key="message.id" class="iq-message-wrapper"
              :class="message.type">
              <IQUserPrompt v-if="message.type === 'user'" :message="message.content" />
              <IQAIResponse v-else-if="message.type === 'ai'" :response="message.content" :isThinking="false"
                :currentReasoning="''" @actionClick="handleActionClick" />
            </div>

            <!-- Custom IQLoader positioned under the last user message when loading (hidden during thinking phase) -->
            <div
              v-if="shouldUseCustomLoader && loaderVisible && chatStore.isLoading && !chatStore.currentResponse && !chatStore.isThinking && chatStore.messages.length > 0"
              class="iq-loader-wrapper">
              <IQLoader :loading="loadingProgress" :modelValue="loaderVisible" @stop="handleLoaderStop"
                @update:modelValue="loaderVisible = $event" />
            </div>

            <!-- Pulse Loader (simple loader for manual prompts, hidden during thinking phase) -->
            <div
              v-if="!shouldUseCustomLoader && chatStore.isLoading && !chatStore.currentResponse && !chatStore.isThinking"
              class="iq-loading-wrapper">
              <div class="iq-pulse-loader">
                <div class="pulse-circle"></div>
              </div>
            </div>

            <!-- Current Thinking/Response (Gemini-style thinking animation) -->
            <div v-if="chatStore.isThinking || (chatStore.isGenerating && chatStore.currentResponse)"
              class="iq-message-wrapper">
              <IQAIResponse :response="chatStore.currentResponse" :isThinking="chatStore.isThinking"
                :currentReasoning="chatStore.currentReasoning" @actionClick="handleActionClick" />
            </div>
          </div>

          <!-- Search Input at Bottom (only in chat state) -->
          <div class="iq-search-container">
            <IQSearch @submit="handleSubmit" :isGenerating="chatStore.isGenerating || chatStore.isLoading"
              @stop="handleStop" />
          </div>
        </div>
      </div>

      <!-- Right Side: Suggestions Navigator (for Role Responsibilities) -->
      <div v-if="showSuggestions" class="iq-suggestions-section" :class="{ 'all-done': isAllDone }"
        id="iq-navigator-container">
        <SuggestResponsibilitiesNavigator :modelValue="true" :roleName="roleName"
          :responsibilities="suggestedResponsibilities" :inline="true"
          :isCreating="iqOverlayStore.isCreatingResponsibility" :creationError="iqOverlayStore.creationError || ''"
          @close="handleNavigatorClose" @create-and-attach="handleCreateAndAttach" @reject="handleReject"
          @suggest-more="handleSuggestMore" @edit="handleEdit" @is-all-done="handleIsAllDone" />
      </div>

      <!-- Right Side: Checkpoint Suggestions Navigator (for Responsibility Checkpoints) -->
      <div v-if="showCheckpointSuggestions" class="iq-suggestions-section" :class="{ 'all-done': isAllCheckpointsDone }"
        id="iq-checkpoint-navigator-container">
        <SuggestCheckpointsNavigator ref="checkpointNavigatorRef" :modelValue="true" :inline="true"
          :responsibilityName="responsibilityName" :checkpoints="suggestedCheckpoints"
          :isCreating="iqOverlayStore.isCreatingCheckpoint" @close="handleCheckpointNavigatorClose"
          @suggest-more="handleSuggestMoreCheckpoints" @add-checkpoint="handleAddCheckpoint"
          @reject-checkpoint="handleRejectCheckpoint" @is-all-done="handleIsAllCheckpointsDone" />
      </div>

      <!-- Right Side: Video Instructions Pane -->
      <div v-if="iqOverlayStore.showVideoInstructionsPane && iqOverlayStore.suggestedInstructions" 
           class="iq-video-instructions-section"
           :class="{ 'iq-video-instructions-section--error': hasVideoInstructionsError }">
        <!-- Header with buttons only when validation passed (no error) -->
        <div v-if="!hasVideoInstructionsError" class="iq-video-instructions-header">
          <div class="iq-video-instructions-actions">
            <StandardButton
              type="magenta"
              variant="outline"
              size="small"
              label="Discard"
              :disabled="isButtonLoading"
              @click="handleVideoInstructionsDiscard"
            />
            <StandardButton
              type="magenta"
              variant="primary"
              size="small"
              label="Save"
              :disabled="isButtonLoading"
              :loading="isButtonLoading"
              @click="handleVideoInstructionsSave"
            />
          </div>
        </div>
        <div class="iq-video-instructions-content">
          <MicrolearningViewEdit
            :video="iqOverlayStore.suggestedInstructions.video_url || iqOverlayStore.videoUrl"
            :error="iqOverlayStore.suggestedInstructions.error || null"
            :summary="iqOverlayStore.suggestedInstructions.summary || null"
            :steps="iqOverlayStore.suggestedInstructions.steps || []"
            :editable="true"
            @summary-click="handleSummaryClick"
            @step-click="handleStepClick"
          />
        </div>
      </div>
    </div>

    <!-- Inspection Step Dialog -->
    <InspectionStepDialog
      :value="showInspectionDialog"
      :state="inspectionDialogState"
      :category="dialogCategory"
      :inspect="dialogInspect"
      :lookFor="dialogLookFor"
      :passIf="dialogPassIf"
      :failIf="dialogFailIf"
      :callApiOnSave="false"
      @input="showInspectionDialog = $event"
      @close="handleInspectionDialogClose"
      @discard="handleInspectionDialogDiscard"
      @submit="handleInspectionDialogSubmit"
    />
  </div>
</template>

<script setup>
import { computed, ref, nextTick, watch, onUnmounted } from "vue";
import IQHeader from "@components/IQHeader/IQHeader.vue";
import IQAskAnythingHeader from "@components/IQAskAnythingHeader/IQAskAnythingHeader.vue";
import IQSearch from "@components/IQSearch/IQSearch.vue";
import IQUserPrompt from "@components/IQUserPrompt/IQUserPrompt.vue";
import IQAIResponse from "@components/IQAIResponse/IQAIResponse.vue";
import IQLoader from "@components/IQLoader/IQLoader.vue";
import Icon from "@components/icons/Icon.vue";
import SuggestResponsibilitiesNavigator from "@components/SuggestResponsibilitiesNavigator/SuggestResponsibilitiesNavigator.vue";
import SuggestCheckpointsNavigator from "@components/SuggestCheckpointsNavigator/SuggestCheckpointsNavigator.vue";
import MicrolearningViewEdit from "@components/MicrolearningViewEdit/MicrolearningViewEdit.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import InspectionStepDialog from "@components/InspectionStepDialog/InspectionStepDialog.vue";
import { useIQChatStore } from "./store/iqChatStore.js";
import { useIQOverlayStore } from "@/store/pinia/iqOverlay";
import { API } from "@/api/apiEndPoints.js";
import configService from "../../account/src/services/config";
import { changeCheckpointV2 } from "@/api/responsibilityAPI";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  welcomeText: {
    type: String,
    default: "What do you want to do today?",
  },
  initialPrompt: {
    type: String,
    default: null,
  },
  type: {
    type: String,
    default: "",
  },
  showSuggestions: {
    type: Boolean,
    default: false,
  },
  roleName: {
    type: String,
    default: "",
  },
  suggestedResponsibilities: {
    type: Array,
    default: () => [],
  },
  useCustomLoader: {
    type: Boolean,
    default: undefined, // If not provided, will be determined by initialPrompt
  },
  // Checkpoint suggestion props
  showCheckpointSuggestions: {
    type: Boolean,
    default: false,
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  suggestedCheckpoints: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "update:modelValue",
  "close",
  "submit",
  "actionClick",
  "create-and-attach",
  "reject",
  "suggest-more",
  "edit",
  "close-navigator",
  "update:suggestions",
  // Checkpoint events (using store pattern for actions, only navigator/UI events via emit)
  "close-checkpoint-navigator",
  "update:checkpoint-suggestions",
  "edit-checkpoint",
  "refresh-checkpoint"
]);

// Stores
const chatStore = useIQChatStore();
const iqOverlayStore = useIQOverlayStore();

// Refs
const messagesContainer = ref(null);
const loadingProgress = ref(0);
const progressInterval = ref(null);
const loaderVisible = ref(false);
const isAllDone = ref(false);
const isAllCheckpointsDone = ref(false);
const checkpointNavigatorRef = ref(null);
const isButtonLoading = ref(false);

// Inspection Step Dialog state
const showInspectionDialog = ref(false);
const selectedStep = ref(null);
const selectedStepIndex = ref(-1);

// Computed: Check if video instructions has an error (validation failed)
const hasVideoInstructionsError = computed(() => {
  return !!(iqOverlayStore.suggestedInstructions && iqOverlayStore.suggestedInstructions.error);
});

// Inspection Step Dialog computed properties
const inspectionDialogState = computed(() => {
  if (!selectedStep.value) return 'Empty';
  return 'Edit';
});

const dialogCategory = computed(() => {
  return selectedStep.value && selectedStep.value.competence_category ? selectedStep.value.competence_category : 'knowledge';
});

const dialogInspect = computed(() => {
  return selectedStep.value && selectedStep.value.inspector_action ? selectedStep.value.inspector_action : '';
});

const dialogLookFor = computed(() => {
  return selectedStep.value && selectedStep.value.look_for ? selectedStep.value.look_for : '';
});

const dialogPassIf = computed(() => {
  return selectedStep.value && selectedStep.value.pass_criteria ? selectedStep.value.pass_criteria : '';
});

const dialogFailIf = computed(() => {
  return selectedStep.value && selectedStep.value.fail_criteria ? selectedStep.value.fail_criteria : '';
});

// Computed: Determine which loader to use
// Use custom loader if initialPrompt is provided (auto-filled), otherwise use pulse loader
const shouldUseCustomLoader = computed(() => {
  // If explicitly set via prop, use that value
  if (props.useCustomLoader !== undefined) {
    return props.useCustomLoader;
  }
  // Otherwise, use custom loader if initialPrompt is provided (auto-filled)
  return !!props.initialPrompt;
});

// Watch for initialPrompt and auto-submit when provided
watch(() => props.initialPrompt, (newPrompt) => {
  if (newPrompt && props.modelValue) {
    // Auto-submit the initial prompt
    nextTick(() => {
      handleSubmit(newPrompt);
    });
  }
}, { immediate: true });

const handleClose = () => {
  emit("update:modelValue", false);
  emit("close");
  // Clear chat when closing
  chatStore.clearChat();
};

const handleSubmit = async (data) => {
  chatStore.addUserMessage(data);

  // Determine which API endpoint to use based on type
  let apiEndpoint = null;
  let customPayload = null;

  // Get account_id from localStorage
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

  // Get the IQ base URL from config (comes from backend secure config)
  const iqBaseUrl = configService.get("iqBaseUrl");

  if (props.type === "videoInstructions") {
    // Use the inspection_instructions/from_video/ endpoint
    apiEndpoint = `${iqBaseUrl.replace(/\/$/, '')}/inspection_instructions/from_video/`;

    // Extract video URL from iframe format
    const extractedVideoUrl = extractVideoUrl(iqOverlayStore.videoUrl);

    // Get business purpose from currentAccountDetails in localStorage
    const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
    const businessPurpose = currentAccountDetails.purpose || "";

    // Construct custom payload for video instructions
    customPayload = {
      video_url: extractedVideoUrl,
      business_purpose: businessPurpose,
      responsibility_name: iqOverlayStore.responsibilityName || "",
      responsibility_purpose: iqOverlayStore.responsibilityPurpose || "",
      checkpoint_criteria: `${iqOverlayStore.checkpointPrefix} ${iqOverlayStore.checkpointCriteria}`.trim()
    };
  } else if (props.type === "roleResponsibility") {
    // Use custom endpoint if provided, otherwise construct URL properly
    // Ensure proper slash joining (remove trailing slash from base, add leading slash to endpoint)
    apiEndpoint = `${iqBaseUrl.replace(/\/$/, '')}/${API.GET_SUGGESTIONS}`;

    // Construct custom payload for roleResponsibility type
    customPayload = {
      account_id: accountId,
      prompt: data,
      suggestion_type: "role_responsibilities"
    };
  } else if (props.type === "responsibilityCheckpoint") {
    // Use custom endpoint if provided, otherwise construct URL properly
    // Ensure proper slash joining (remove trailing slash from base, add leading slash to endpoint)
    apiEndpoint = `${iqBaseUrl.replace(/\/$/, '')}/${API.GET_SUGGESTIONS}`;

    // Construct custom payload for responsibilityCheckpoint type
    customPayload = {
      account_id: accountId,
      prompt: data,
      suggestion_type: "responsibility_checkpoint_criteria"
    };
  }

  // Generate AI response from backend API with appropriate endpoint and payload
  await chatStore.generateAIResponse(data, 5, apiEndpoint, customPayload);
};

// Extract video URL from iframe HTML format
// Input: <iframe src="https://embed.api.video/vod/vi4F5DkE4590VkCwsOMfRL5w" ...></iframe>
// Output: https://embed.api.video/vod/vi4F5DkE4590VkCwsOMfRL5w
const extractVideoUrl = (iframeHtml) => {
  if (!iframeHtml) return "";
  
  // If it's already a direct URL (not iframe), return as is
  if (!iframeHtml.includes('<iframe') && !iframeHtml.includes('src=')) {
    return iframeHtml;
  }
  
  // Extract src from iframe - handle both escaped quotes \" and regular quotes "
  const srcMatch = iframeHtml.match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) {
    return srcMatch[1];
  }
  
  // Fallback: try without escaped quotes
  const fallbackMatch = iframeHtml.match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) {
    return fallbackMatch[1];
  }
  
  return "";
};

const handleStop = () => {
  stopProgressAnimation();
  hideLoader();
  chatStore.stopGenerating();
};

const handleLoaderStop = () => {
  // Immediately hide loader and stop animation
  stopProgressAnimation();
  hideLoader();
  // Cancel the API request
  chatStore.stopGenerating();
};

// Loader visibility management
const showLoader = () => {
  loaderVisible.value = true;
};

const hideLoader = () => {
  loaderVisible.value = false;
};

// Progress animation constants
const PROGRESS_ANIMATION = {
  TARGET_TIME_MS: 30000, // 30 seconds to reach initial progress threshold
  INITIAL_MAX_PROGRESS: 92, // Cap at 92% until API responds
  FINAL_MAX_PROGRESS: 95, // Cap at 95% during slow crawl phase
  SLOW_CRAWL_DURATION_MS: 20000, // Duration for slow crawl phase
  SLOW_CRAWL_INCREMENT: 3, // Additional progress during slow crawl (92% to 95%)
  UPDATE_INTERVAL_MS: 50, // Update every 50ms for smoother animation
};

// Progress animation logic - reaches ~90% over ~30 seconds, then slows down
const startProgressAnimation = () => {
  stopProgressAnimation(); // Clear any existing interval
  loadingProgress.value = 0;
  showLoader();

  const startTime = Date.now();
  const targetTime = PROGRESS_ANIMATION.TARGET_TIME_MS;
  const maxProgress = PROGRESS_ANIMATION.INITIAL_MAX_PROGRESS;

  progressInterval.value = setInterval(() => {
    if (!chatStore.isLoading || chatStore.currentResponse) {
      stopProgressAnimation();
      return;
    }

    const elapsed = Date.now() - startTime;

    if (elapsed < targetTime) {
      // First phase: reach ~90% over 30 seconds
      // Use easing function for smooth animation
      const progress = (elapsed / targetTime) * maxProgress;
      loadingProgress.value = Math.min(progress, maxProgress);
    } else {
      // Second phase: slow crawl from 92% to 95% over additional time
      // This keeps the bar moving while waiting for API response
      const extraTime = elapsed - targetTime;
      const slowProgress = maxProgress + Math.min((extraTime / PROGRESS_ANIMATION.SLOW_CRAWL_DURATION_MS) * PROGRESS_ANIMATION.SLOW_CRAWL_INCREMENT, PROGRESS_ANIMATION.SLOW_CRAWL_INCREMENT);
      loadingProgress.value = Math.min(slowProgress, PROGRESS_ANIMATION.FINAL_MAX_PROGRESS);
    }
  }, PROGRESS_ANIMATION.UPDATE_INTERVAL_MS);
};

const stopProgressAnimation = () => {
  if (progressInterval.value) {
    clearInterval(progressInterval.value);
    progressInterval.value = null;
  }
  loadingProgress.value = 0;
};

const handleActionClick = (action) => {
  // Handle review-suggestions action to refresh responsibility suggestions
  if (action.type === "review-suggestions") {

    // First, close the navigator
    emit("close-navigator");

    // Then submit a new query to refresh suggestions with role info
    nextTick(() => {
      const refreshPrompt = `Pls refresh suggestions for Role ${props.roleName}`;
      handleSubmit(refreshPrompt);
    });
  }

  // Handle view-video-instructions action to show the right pane
  if (action.type === "view-video-instructions") {
    // Show video instructions pane
    iqOverlayStore.showVideoInstructionsPane = true;
  }

  // Handle refresh-checkpoint-suggestions action to refresh checkpoint suggestions
  if (action.type === "refresh-checkpoint-suggestions") {

    // First, close the checkpoint navigator
    emit("close-checkpoint-navigator");
    iqOverlayStore.closeCheckpointNavigator();

    // Then submit a new query to refresh checkpoint suggestions
    nextTick(() => {
      const refreshPrompt = `Please suggest more Checkpoints for Responsibility ${props.responsibilityName}`;
      handleSubmit(refreshPrompt);
    });
  }

  // Emit action to parent for handling
  emit("actionClick", action);
};

const handleNavigatorClose = () => {
  emit("close-navigator");
};

const handleCreateAndAttach = (data) => {
  emit("create-and-attach", data);
};

const handleReject = (data) => {
  emit("reject", data);
};

const handleSuggestMore = () => {

  // Close the navigator first
  emit("close-navigator");

  // Submit refresh query to get new suggestions with role info
  nextTick(() => {
    const refreshPrompt = `Pls refresh suggestions for Role ${props.roleName}`;
    handleSubmit(refreshPrompt);
  });

  emit("suggest-more");
};

const handleEdit = (data) => {
  emit("edit", data);
};

const handleIsAllDone = (value) => {
  isAllDone.value = value;
};

// Video instructions handlers
const handleVideoInstructionsClose = () => {
  iqOverlayStore.closeVideoInstructionsPane();
};

const handleVideoInstructionsSave = async () => {
  // Get account_id from localStorage
  const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
  const responsibilityId = iqOverlayStore.responsibilityId;
  const checkpointId = iqOverlayStore.checkpointId;
  const instructionsData = iqOverlayStore.suggestedInstructions;
  if (!accountId || !responsibilityId || !checkpointId) {
    console.error("Missing required IDs for saving video instructions");
    return;
  }
  
  try {
    // Prepare the microlearning_instructions payload
    const payload = {
      microlearning_instructions: instructionsData
    };
    isButtonLoading.value = true;
    await changeCheckpointV2(accountId, responsibilityId, checkpointId, payload);
    // Emit refresh event via store to update checkpoint data
    iqOverlayStore.emitRefreshCheckpoint();
    // Also emit via component event
    emit("refresh-checkpoint");
    // Close the overlay after successful save
    handleClose();
  } catch (error) {
    isButtonLoading.value = false;
    console.error("Error saving video instructions:", error);
    // Still close the overlay on error
    handleClose();
  } finally {
    isButtonLoading.value = false;
  }
};

const handleVideoInstructionsDiscard = () => {
  // Discard changes and close the entire overlay
  handleClose();
};

const handleSummaryClick = (summary) => {
  console.log("Summary clicked:", summary);
  // Future: open summary edit dialog
};

const handleStepClick = ({ step, index }) => {
  // Open inspection step dialog for editing
  selectedStep.value = { ...step };
  selectedStepIndex.value = index;
  showInspectionDialog.value = true;
};

// Checkpoint Navigator Handlers
const handleCheckpointNavigatorClose = () => {
  emit("close-checkpoint-navigator");
  iqOverlayStore.closeCheckpointNavigator();
};

const handleAddCheckpoint = (data) => {
  // Only emit through the store for subscribers (EditCheckpointsTab)
  // Don't use Vue emit as it's not handled by App.vue and causes duplicate calls
  iqOverlayStore.emitAddCheckpoint(data);
};

const handleRejectCheckpoint = (data) => {
  // Only emit through the store for subscribers
  iqOverlayStore.emitRejectCheckpoint(data);
};

const handleSuggestMoreCheckpoints = () => {
  // Close the navigator first
  emit("close-checkpoint-navigator");
  iqOverlayStore.closeCheckpointNavigator();

  // Submit refresh query to get new suggestions with responsibility info
  nextTick(() => {
    const refreshPrompt = `Please suggest more Checkpoints for Responsibility ${props.responsibilityName}`;
    handleSubmit(refreshPrompt);
  });

  // Only emit through the store for subscribers
  iqOverlayStore.emitSuggestMoreCheckpoints();
};

const handleEditCheckpoint = (data) => {
  emit("edit-checkpoint", data);
};

const handleIsAllCheckpointsDone = (value) => {
  isAllCheckpointsDone.value = value;
};

// Inspection Step Dialog handlers
const handleInspectionDialogClose = () => {
  showInspectionDialog.value = false;
  selectedStep.value = null;
  selectedStepIndex.value = -1;
};

const handleInspectionDialogDiscard = () => {
  // Just close without saving
  handleInspectionDialogClose();
};

const handleInspectionDialogSubmit = (formData) => {
  // Update the step in the store's suggestedInstructions
  if (iqOverlayStore.suggestedInstructions && 
      selectedStepIndex.value >= 0 && 
      iqOverlayStore.suggestedInstructions.steps &&
      selectedStepIndex.value < iqOverlayStore.suggestedInstructions.steps.length) {
    
    // Create updated instructions object with the modified step
    const updatedInstructions = {
      ...iqOverlayStore.suggestedInstructions,
      steps: [
        ...iqOverlayStore.suggestedInstructions.steps.slice(0, selectedStepIndex.value),
        {
          ...iqOverlayStore.suggestedInstructions.steps[selectedStepIndex.value],
          competence_category: formData.category,
          inspector_action: formData.inspect,
          look_for: formData.lookFor,
          pass_criteria: formData.passIf,
          fail_criteria: formData.failIf,
        },
        ...iqOverlayStore.suggestedInstructions.steps.slice(selectedStepIndex.value + 1),
      ]
    };
    
    // Update the store (this will reflect in MicrolearningViewEdit component)
    iqOverlayStore.suggestedInstructions = updatedInstructions;
  }
  
  // Close the dialog
  handleInspectionDialogClose();
};

// Auto scroll to bottom when new messages arrive
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

// Watch for new messages and scroll
watch(() => chatStore.messages.length, scrollToBottom);
watch(() => chatStore.currentResponse, scrollToBottom);

// Watch for loading state to start/stop progress animation (only for custom loader)
watch(() => chatStore.isLoading, (isLoading, wasLoading) => {
  if (shouldUseCustomLoader.value) {
    if (isLoading && !chatStore.currentResponse && chatStore.messages.length > 0) {
      startProgressAnimation();
    } else if (!isLoading && wasLoading) {
      // API call completed - complete the progress bar
      if (loadingProgress.value < 100) {
        loadingProgress.value = 100;
      }
      setTimeout(() => {
        stopProgressAnimation();
        hideLoader();
      }, 200); // Small delay to show completion
    } else if (!isLoading) {
      stopProgressAnimation();
      hideLoader();
    }
  }
});

// Watch for currentResponse to stop animation when response arrives (only for custom loader)
watch(() => chatStore.currentResponse, (response, oldResponse) => {
  if (shouldUseCustomLoader.value && response && !oldResponse) {
    // Response just arrived - complete the progress bar to 100%
    loadingProgress.value = 100;
    setTimeout(() => {
      stopProgressAnimation();
      hideLoader();
    }, 200); // Small delay to show completion
  }
});

// Cleanup on unmount
onUnmounted(() => {
  stopProgressAnimation();
});

// Watch for when generation completes to auto-open navigator
watch(() => chatStore.isGenerating, (isGenerating, wasGenerating) => {

  // When generation completes (was generating, now not)
  if (wasGenerating && !isGenerating) {
    // Get the last AI message (the one just added)
    const lastMessage = chatStore.messages
      .filter(msg => msg.type === 'ai')
      .pop();

    // Check if we have video instruction suggestions
    if (lastMessage && 
        lastMessage.content &&
        lastMessage.content.suggestions && 
        lastMessage.content.suggestions.steps &&
        Array.isArray(lastMessage.content.suggestions.steps)) {
      
      // This is a video instructions response - set the instructions and show pane
      nextTick(() => {
        iqOverlayStore.setSuggestedInstructions(lastMessage.content.suggestions);
      });
      return;
    }

    // Check if we have suggestions in the last message
    if (lastMessage &&
      lastMessage.content &&
      lastMessage.content.suggestions &&
      Array.isArray(lastMessage.content.suggestions) &&
      lastMessage.content.suggestions.length > 0) {

      // Determine type from the response suggestionType
      const suggestionType = lastMessage.content.suggestionType;

      // Handle based on suggestion type from the response
      if (suggestionType === "responsibility_checkpoint_criteria") {
        // Transform suggestions to the format expected by SuggestCheckpointsNavigator
        // Map proficiency_level (1, 2, 3, 4) to level names
        const levelMap = {
          1: "Apprentice",
          2: "Professional",
          3: "Coach",
          4: "Master"
        };

        const transformedCheckpoints = lastMessage.content.suggestions.map(item => ({
          criteria: item.criteria || item.checkpoint_criteria || "",
          // Map 'purpose' from backend to 'instructions' for the navigator
          instructions: item.instruction || item.instructions || item.purpose || item.checkpoint_instructions || "",
          level: levelMap[item.proficiency_level] || item.level || "Apprentice",
          // Include prefix from backend response
          prefix: item.prefix || ""
        }));

        // Emit to parent to update checkpoint suggestions and show navigator
        nextTick(() => {
          emit('update:checkpoint-suggestions', {
            suggestions: transformedCheckpoints,
            responsibilityName: props.responsibilityName
          });
        });
      } else {
        // Transform suggestions to the format expected by SuggestResponsibilitiesNavigator
        const transformedSuggestions = lastMessage.content.suggestions.map(item => ({
          name: item.responsibility_name,
          purpose: item.responsibility_purpose
        }));

        // Emit to parent to update suggestions and show navigator
        nextTick(() => {
          emit('update:suggestions', {
            suggestions: transformedSuggestions,
            roleName: props.roleName  // Always use the original roleName prop, not the API response
          });
        });
      }
    } else {
      console.log("No suggestions found in last message");
    }
  }
});
</script>

<style>
.iq-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.iq-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #212121;
}

/* Header */
.iq-overlay-header {
  position: relative;
  width: 100%;
  z-index: 10;
  background: #212121;
  flex-shrink: 0;
}

/* Body Area */
.iq-overlay-body {
  position: relative;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  z-index: 1;
  isolation: isolate;
}

.iq-overlay-body.split-layout {
  display: flex;
  flex-direction: row;
}

/* Chat Section */
.iq-chat-section {
  width: 100%;
  height: 100%;
  background: #212121;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
}

.iq-chat-section.with-suggestions {
  width: 50%;
  flex: 0 0 50%;
}

.iq-chat-section.with-video-instructions {
  width: 30%;
  flex: 0 0 30%;
}

.iq-chat-section.with-video-instructions-error {
  width: 50%;
  flex: 0 0 50%;
}

/* Suggestions Section */
.iq-suggestions-section {
  width: 50%;
  flex: 0 0 50%;
  height: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
}

.iq-suggestions-section .responsibility-suggestion-modal {
  height: 86vh !important;
}

.iq-suggestions-section.all-done .responsibility-suggestion-modal {
  height: auto !important;
  min-height: fit-content;
}

/* Checkpoint suggestion modal styles */
.iq-suggestions-section .checkpoint-suggestion-modal {
  height: 86vh !important;
}

.iq-suggestions-section.all-done .checkpoint-suggestion-modal {
  height: auto !important;
  min-height: fit-content;
}

/* Navigator container - fills the right section */
#iq-navigator-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

/* Checkpoint Navigator container - fills the right section */
#iq-checkpoint-navigator-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: auto;
  padding-right: 20px;
  display: flex;
  flex-direction: column;
}

/* Welcome State - Centered Layout */
.iq-welcome-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  max-width: 650px;
  margin: 0 auto;
  width: 100%;
}

.iq-welcome-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  width: 100%;
  max-width: 650px;
}

.iq-search-center {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Chat State - Full Height Layout */
.iq-chat-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 650px;
  margin: 0 auto;
  width: 100%;
}

.iq-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px 0px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;
  /* Hide scrollbar but keep scrolling functionality */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* Internet Explorer 10+ */
}

.iq-chat-messages::-webkit-scrollbar {
  display: none;
  /* WebKit browsers (Chrome, Safari, Edge) */
}

.iq-message-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.iq-message-wrapper.user {
  align-items: flex-end;
}

.iq-message-wrapper.ai {
  align-items: flex-start;
}

.iq-loader-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 0;
  margin-left: 0;
}

.iq-loading-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
}

.iq-pulse-loader {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffff;
  animation: pulse 0.8s infinite ease-in-out;
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(0.85);
    opacity: 0.6;
  }

  50% {
    transform: scale(1.15);
    opacity: 1;
  }
}

.iq-search-container {
  padding: 16px 20px 20px 20px;
  background: #212121;
  display: flex;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
}

/* Video Instructions Section */
.iq-video-instructions-section {
  width: calc(70% - 24px);
  flex: 0 0 calc(70% - 24px);
  height: calc(100% - 24px);
  position: relative;
  z-index: 1;
  overflow: hidden;
  isolation: isolate;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  margin-right: 24px;
  margin-bottom: 24px;
}

/* Error state: 50-50 width, no header */
.iq-video-instructions-section--error {
  width: calc(50% - 24px);
  flex: 0 0 calc(50% - 24px);
}

.iq-video-instructions-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 20px 24px;
  border-bottom: 1px solid #EAECF0;
  flex-shrink: 0;
}

.iq-video-instructions-title {
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  margin: 0;
}

.iq-video-instructions-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.iq-video-instructions-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #667085;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.iq-video-instructions-close:hover {
  background-color: #F2F4F7;
  color: #344054;
}

.iq-video-instructions-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}
</style>
